import {useContext, useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {StoreFrontScriptManagerDetailsContext} from '../../context'
import {InputText} from '../../../../components/InputText'
import {TextArea} from '../../../../components/TextArea'
import {Select} from '../../../../components/Select/'
import ScriptStyleEditor from '../../../../components/ScriptStyleEditor'
import Loading from '../../../loading'
import { useNavigate } from 'react-router-dom'

const scriptSchema = yup.object().shape({
  name: yup
    .string()
    .required('Script name is required')
    .max(100, 'Script name cannot exceed 100 characters'),
  description: yup.string().max(250, 'Description cannot exceed 250 characters'),
})

const placementOptions = [
  {value: 'footer', label: 'Footer'},
  {value: 'header', label: 'Header'},
]

const loadMethodOptions = [
  {value: 'default', label: 'Default'},
  {value: 'async', label: 'Async'},
  {value: 'defer', label: 'Defer'},
]

const ScriptForm = () => {
  const navigate = useNavigate()
  const {script, isLoading, isOperationLoading, updateScript, createScript, hasWritePermission} = useContext(
    StoreFrontScriptManagerDetailsContext
  )

  const [isEditorValid, setIsEditorValid] = useState(true)
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: {errors, isValid,isDirty},
    reset,
    setError,
  } = useForm({
    resolver: yupResolver(scriptSchema),
    defaultValues: {
      name: '',
      description: '',
      placement: 'footer',
      load_method: 'default',
      content: '',
    },
    mode: 'onChange',
  })

  const watchedContent = watch('content')

  useEffect(() => {
    if (script) {
      reset({
        name: script?.name,
        description: script?.description || '',
        placement: script?.placement,
        load_method: script?.load_method,
        content: script?.content,
      })
    }
  }, [script, reset])

  const onSubmit = async (data: any) => {
    if (script?.id) {
      await updateScript(data, script?.id, setError)
      navigate('/storefronts/script-manager')
    } else {
      await createScript(data, setError)
      navigate('/storefronts/script-manager')
      reset()
    }
  }

  const isFormValid = isValid && isEditorValid && !isOperationLoading && isDirty && hasWritePermission

  return (
    <div className='position-relative'>
      <form onSubmit={handleSubmit(onSubmit)} className='form'>
        <div className='row'>
          <div className='col-6'>
            <InputText
              label='Script Name'
              placeholder='Enter script name'
              isRequired
              error={errors.name}
              register={register('name')}
              className='mb-8'
              maxLength={100}
              id={'ScriptName'}
              disabled={!hasWritePermission}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-6'>
            <TextArea
              label='Script Description'
              placeholder='Enter script description (optional)'
              error={errors.description}
              register={register('description')}
              className='mb-8'
              maxLength={250}
              rows={4}
              id={'ScriptDescription'}
              disabled={!hasWritePermission}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-2'>
            <Select
              label='Placement'
              register={register('placement')}
              className='mb-8'
              options={placementOptions}
              placeholder='Select placement'
              id={'ScriptPlacement'}
              disabled={!hasWritePermission}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-2'>
            <Select
              label='Load method'
              register={register('load_method')}
              className='mb-8'
              options={loadMethodOptions}
              placeholder='Select load method'
              id={'ScriptLoadMethod'}
              disabled={!hasWritePermission}
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-12'>
            <div className='mb-8'>
              <label className='form-label required'>Script Content</label>
              <ScriptStyleEditor
                value={watchedContent}
                onChange={(value) => {
                  setValue('content', value, {shouldDirty: true})
                }}
                error={errors.content?.message || ''}
                className='mt-2'
                isRequired
                onValidChange={setIsEditorValid}
                disabled={!hasWritePermission}
              />
            </div>
          </div>
        </div>

        <div>
          <button type='submit' className='btn btn-primary' disabled={!isFormValid}>
            {isOperationLoading ? 'Saving...' : script?.id ? 'Update Script' : 'Create Script'}
          </button>
        </div>
      </form>
      {isLoading && <Loading />}
    </div>
  )
}

export default ScriptForm
